import { Header, notify } from "@general-backoffice/core";
import type {
  EmailSystemStatus,
  EmailTestPreview,
  EmailTestSearchUser,
  EmailTriggerMetadata,
} from "@investhinks/api-client";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { emailTemplatesEndpoints } from "../../../../api";

interface BulkResult {
  trigger: string;
  success: boolean;
  error?: string;
}

interface BulkResults {
  successCount: number;
  failCount: number;
  totalSent: number;
  results: BulkResult[];
}

interface BulkProgress {
  current: number;
  total: number;
}

const EmailTesting: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "emailTesting" });

  // System status
  const [systemStatus, setSystemStatus] = useState<EmailSystemStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(true);

  // Available triggers
  const [triggers, setTriggers] = useState<EmailTriggerMetadata[]>([]);
  const [isLoadingTriggers, setIsLoadingTriggers] = useState<boolean>(true);

  // User search
  const [userSearchQuery, setUserSearchQuery] = useState<string>("");
  const [userSearchResults, setUserSearchResults] = useState<EmailTestSearchUser[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<EmailTestSearchUser | null>(null);
  const [customEmail, setCustomEmail] = useState<string>(() => localStorage.getItem("emailTesting_customEmail") || "");

  // Selected trigger for single test
  const [selectedTrigger, setSelectedTrigger] = useState<EmailTriggerMetadata | null>(null);

  // Preview modal
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<EmailTestPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);

  // Sending state
  const [isSending, setIsSending] = useState<boolean>(false);

  // Bulk send
  const [selectedTriggersForBulk, setSelectedTriggersForBulk] = useState<string[]>([]);
  const [isSendingBulk, setIsSendingBulk] = useState<boolean>(false);
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);
  const [bulkResults, setBulkResults] = useState<BulkResults | null>(null);

  // Load system status and triggers on mount
  useEffect(() => {
    loadSystemStatus();
    loadTriggers();
  }, []);

  // Debounced user search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery.length >= 2) {
        searchUsers(userSearchQuery);
      } else {
        setUserSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  const loadSystemStatus = async () => {
    try {
      setIsLoadingStatus(true);
      const { data: statusData } = await emailTemplatesEndpoints.getSystemStatus();
      setSystemStatus(statusData);
    } catch (err: unknown) {
      console.error("Failed to load system status:", err);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const loadTriggers = async () => {
    try {
      setIsLoadingTriggers(true);
      const { data: triggersData } = await emailTemplatesEndpoints.getAllTriggers();
      setTriggers(triggersData || []);
    } catch (err: unknown) {
      console.error("Failed to load triggers:", err);
    } finally {
      setIsLoadingTriggers(false);
    }
  };

  const searchUsers = async (query: string) => {
    try {
      setIsSearchingUsers(true);
      const { data: usersData } = await emailTemplatesEndpoints.searchUsers(query);
      setUserSearchResults(usersData || []);
    } catch (err: unknown) {
      console.error("Failed to search users:", err);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const handleSelectUser = (user: EmailTestSearchUser) => {
    setSelectedUser(user);
    setCustomEmail(user.email);
    setUserSearchQuery("");
    setUserSearchResults([]);
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setCustomEmail("");
  };

  const handlePreview = async (trigger: EmailTriggerMetadata) => {
    try {
      setIsLoadingPreview(true);
      setSelectedTrigger(trigger);
      // Note: previewTestEmail requires userId, use 0 as fallback for no user
      const userId = typeof selectedUser?.id === "number" ? selectedUser.id : 0;
      const { data: previewResult } = await emailTemplatesEndpoints.previewTestEmail(trigger.trigger, userId);
      setPreviewData(previewResult);
      setPreviewModalOpen(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load preview";
      notify.error(errorMessage);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSendTest = async (trigger: EmailTriggerMetadata | null) => {
    if (!trigger) return;
    if (!selectedUser && !customEmail) {
      notify.error(t("selectUserFirst") || "Please select a user or enter an email address");
      return;
    }

    try {
      setIsSending(true);
      setSelectedTrigger(trigger);
      if (customEmail) {
        localStorage.setItem("emailTesting_customEmail", customEmail);
      }
      // sendTestToUser takes a single input object
      await emailTemplatesEndpoints.sendTestToUser({
        trigger: trigger.trigger,
        userId: typeof selectedUser?.id === "number" ? selectedUser.id : 0,
        customEmail: customEmail || undefined,
      });
      notify.success(t("testEmailSent") || `Test email for "${trigger.label.en}" sent successfully`);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to send test email";
      notify.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleBulkSend = async () => {
    if (!selectedUser && !customEmail) {
      notify.error(t("selectUserFirst") || "Please select a user or enter an email address");
      return;
    }

    const triggersToSend =
      selectedTriggersForBulk.length > 0 ? selectedTriggersForBulk : triggers.map((tr) => tr.trigger);

    try {
      setIsSendingBulk(true);
      setBulkProgress({ current: 0, total: triggersToSend.length });

      if (customEmail) {
        localStorage.setItem("emailTesting_customEmail", customEmail);
      }
      const { data: bulkData } = await emailTemplatesEndpoints.sendAllTestsToUser({
        userId: typeof selectedUser?.id === "number" ? selectedUser.id : 0,
        customEmail: customEmail || undefined,
        triggers: selectedTriggersForBulk.length > 0 ? selectedTriggersForBulk : undefined,
      });

      // The API returns { success: boolean; sent: number }, adapt to BulkResults
      const adaptedResults: BulkResults = {
        successCount: bulkData.sent,
        failCount: 0,
        totalSent: bulkData.sent,
        results: [],
      };
      setBulkResults(adaptedResults);
      notify.success(`Sent ${bulkData.sent} test emails`);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to send bulk test emails";
      notify.error(errorMessage);
    } finally {
      setIsSendingBulk(false);
      setBulkProgress(null);
    }
  };

  const toggleTriggerForBulk = (trigger: string) => {
    setSelectedTriggersForBulk((prev) => {
      if (prev.includes(trigger)) {
        return prev.filter((tr) => tr !== trigger);
      }
      return [...prev, trigger];
    });
  };

  const selectAllTriggers = () => {
    setSelectedTriggersForBulk(triggers.map((tr) => tr.trigger));
  };

  const deselectAllTriggers = () => {
    setSelectedTriggersForBulk([]);
  };

  const recipientEmail = customEmail || selectedUser?.email;

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12}>
          <h2 className="text-white mb-0">{t("title") || "Email Testing"}</h2>
        </Col>
      </Header.BreadCrumb>

      <Container className="mt--6" fluid>
        {/* System Status Card */}
        <Row className="mb-4">
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">
                  <i className="fas fa-cog mr-2"></i>
                  {t("systemStatus") || "System Status"}
                </h3>
                <Button color="secondary" size="sm" onClick={loadSystemStatus} disabled={isLoadingStatus}>
                  <i className="fas fa-sync-alt mr-1"></i>
                  {t("refresh") || "Refresh"}
                </Button>
              </CardHeader>
              <CardBody>
                {isLoadingStatus ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" />
                  </div>
                ) : systemStatus ? (
                  <div>
                    <Alert color={systemStatus.notificationsEnabled ? "success" : "warning"} className="mb-0">
                      <div className="d-flex align-items-center">
                        <i
                          className={`fas fa-${
                            systemStatus.notificationsEnabled ? "check-circle" : "pause-circle"
                          } fa-2x mr-3`}
                        ></i>
                        <div>
                          <strong>
                            {systemStatus.notificationsEnabled
                              ? t("notificationsEnabled") || "Email Notifications ENABLED"
                              : t("notificationsDisabled") || "Email Notifications DISABLED"}
                          </strong>
                          <p className="mb-0 mt-1">{systemStatus.message}</p>
                          <small className="text-muted">
                            {t("envVariable") || "Environment variable"}: {systemStatus.envVariable} ={" "}
                            {systemStatus.currentValue}
                          </small>
                        </div>
                      </div>
                    </Alert>
                    {!systemStatus.notificationsEnabled && (
                      <Alert color="info" className="mt-3 mb-0">
                        <i className="fas fa-info-circle mr-2"></i>
                        {t("testEmailsStillWork") ||
                          "Test emails from this page will still work even when notifications are disabled."}
                      </Alert>
                    )}
                  </div>
                ) : (
                  <Alert color="danger" className="mb-0">
                    {t("failedToLoadStatus") || "Failed to load system status"}
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* User Selection */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <CardHeader>
                <h4 className="mb-0">
                  <i className="fas fa-user mr-2"></i>
                  {t("selectRecipient") || "Select Recipient"}
                </h4>
              </CardHeader>
              <CardBody>
                {selectedUser ? (
                  <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                    <div>
                      <strong>{selectedUser.displayName}</strong>
                      <br />
                      <span className="text-muted">{selectedUser.email}</span>
                      <Badge color="info" className="ml-2">
                        {selectedUser.language?.toUpperCase()}
                      </Badge>
                    </div>
                    <Button color="link" className="text-danger" onClick={clearSelectedUser}>
                      <i className="fas fa-times"></i>
                    </Button>
                  </div>
                ) : (
                  <FormGroup>
                    <Label for="email-testing-field-1">{t("searchUser") || "Search User"}</Label>
                    <div className="position-relative">
                      <Input
                        type="text"
                        placeholder={t("searchPlaceholder") || "Search by email, username, or name..."}
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                      />
                      {isSearchingUsers && (
                        <Spinner size="sm" className="position-absolute" style={{ right: "10px", top: "10px" }} />
                      )}
                    </div>
                    {userSearchResults.length > 0 && (
                      <div className="border rounded mt-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {userSearchResults.map((user) => (
                          <div
                            key={user.id}
                            className="p-2 border-bottom cursor-pointer hover-bg-light"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleSelectUser(user)}
                          >
                            <strong>{user.displayName}</strong>
                            <br />
                            <small className="text-muted">{user.email}</small>
                          </div>
                        ))}
                      </div>
                    )}
                  </FormGroup>
                )}

                <FormGroup className="mt-3">
                  <Label>{t("orEnterEmail") || "Or enter email address"}</Label>
                  <Input id="email-testing-field-1"
                    type="email"
                    placeholder="test@example.com"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    disabled={!!selectedUser}
                  />
                  <small className="text-muted">
                    {t("emailOverride") || "This will override the selected user's email"}
                  </small>
                </FormGroup>

                {recipientEmail && (
                  <Alert color="success" className="mb-0 mt-3">
                    <i className="fas fa-envelope mr-2"></i>
                    {t("willSendTo") || "Test emails will be sent to"}: <strong>{recipientEmail}</strong>
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Bulk Send Card */}
          <Col md={6}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fas fa-paper-plane mr-2"></i>
                  {t("bulkSend") || "Bulk Send"}
                </h4>
                <div>
                  <Button color="link" size="sm" onClick={selectAllTriggers} disabled={isSendingBulk}>
                    {t("selectAll") || "Select All"}
                  </Button>
                  <Button color="link" size="sm" onClick={deselectAllTriggers} disabled={isSendingBulk}>
                    {t("deselectAll") || "Deselect All"}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <p className="text-muted mb-3">
                  {t("bulkSendDescription") ||
                    "Send test emails for multiple triggers at once. Select the triggers below or send all."}
                </p>

                <div style={{ maxHeight: "200px", overflowY: "auto" }} className="mb-3">
                  {triggers.map((trigger) => (
                    <FormGroup check key={trigger.trigger} className="mb-2">
                      <Label check className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          checked={selectedTriggersForBulk.includes(trigger.trigger)}
                          onChange={() => toggleTriggerForBulk(trigger.trigger)}
                          disabled={isSendingBulk}
                        />
                        <span className="ml-2">{trigger.label.en}</span>
                      </Label>
                    </FormGroup>
                  ))}
                </div>

                <div className="text-muted small mb-3">
                  {selectedTriggersForBulk.length > 0
                    ? `${selectedTriggersForBulk.length} ${t("triggersSelected") || "triggers selected"}`
                    : t("allTriggersWillBeSent") || "All triggers will be sent if none selected"}
                </div>

                {bulkProgress && (
                  <Progress value={(bulkProgress.current / bulkProgress.total) * 100} className="mb-3" />
                )}

                <Button color="warning" onClick={handleBulkSend} disabled={isSendingBulk || !recipientEmail} block>
                  {isSendingBulk ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t("sending") || "Sending..."}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      {t("sendBulkTest") || "Send Bulk Test"} ({selectedTriggersForBulk.length || triggers.length})
                    </>
                  )}
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Bulk Results */}
        {bulkResults && (
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">
                    <i className="fas fa-clipboard-list mr-2"></i>
                    {t("bulkResults") || "Bulk Send Results"}
                  </h4>
                  <Button color="secondary" size="sm" onClick={() => setBulkResults(null)}>
                    <i className="fas fa-times mr-1"></i>
                    {t("dismiss") || "Dismiss"}
                  </Button>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Badge color="success" className="mr-2">
                      {bulkResults.successCount} {t("sent") || "sent"}
                    </Badge>
                    <Badge color="danger">
                      {bulkResults.failCount} {t("failed") || "failed"}
                    </Badge>
                  </div>
                  <Table size="sm" responsive>
                    <thead>
                      <tr>
                        <th>{t("trigger") || "Trigger"}</th>
                        <th>{t("status") || "Status"}</th>
                        <th>{t("error") || "Error"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResults.results.map((result) => (
                        <tr key={result.trigger}>
                          <td>{result.trigger}</td>
                          <td>
                            <Badge color={result.success ? "success" : "danger"}>
                              {result.success ? t("sent") || "Sent" : t("failed") || "Failed"}
                            </Badge>
                          </td>
                          <td className="text-danger small">{result.error || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Triggers List */}
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <h3 className="mb-0">
                  <i className="fas fa-list mr-2"></i>
                  {t("emailTriggers") || "Email Triggers"}
                </h3>
              </CardHeader>
              <CardBody>
                {isLoadingTriggers ? (
                  <div className="text-center py-5">
                    <Spinner />
                    <p className="mt-2">{t("loadingTriggers") || "Loading triggers..."}</p>
                  </div>
                ) : (
                  <Table responsive hover>
                    <thead className="thead-light">
                      <tr>
                        <th>{t("trigger") || "Trigger"}</th>
                        <th>{t("description") || "Description"}</th>
                        <th>{t("variables") || "Variables"}</th>
                        <th className="text-center">{t("actions") || "Actions"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {triggers.map((trigger) => (
                        <tr key={trigger.trigger}>
                          <td>
                            <strong>{trigger.label.en}</strong>
                            <br />
                            <code className="small text-muted">{trigger.trigger}</code>
                          </td>
                          <td className="text-muted small">{trigger.description}</td>
                          <td>
                            {trigger.variables.map((v) => (
                              <Badge key={v} color="secondary" className="mr-1 mb-1">
                                {`{{${v}}}`}
                              </Badge>
                            ))}
                          </td>
                          <td className="text-center">
                            <Button
                              color="info"
                              size="sm"
                              className="mr-2"
                              onClick={() => handlePreview(trigger)}
                              disabled={isLoadingPreview && selectedTrigger?.trigger === trigger.trigger}
                            >
                              {isLoadingPreview && selectedTrigger?.trigger === trigger.trigger ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="fas fa-eye"></i>
                              )}
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => handleSendTest(trigger)}
                              disabled={isSending || !recipientEmail}
                            >
                              {isSending && selectedTrigger?.trigger === trigger.trigger ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="fas fa-paper-plane"></i>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Preview Modal */}
      <Modal isOpen={previewModalOpen} toggle={() => setPreviewModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setPreviewModalOpen(false)}>
          {t("emailPreview") || "Email Preview"}: {previewData?.triggerLabel}
        </ModalHeader>
        <ModalBody>
          {previewData && (
            <Row>
              <Col md={4}>
                <h5>{t("testData") || "Test Data"}</h5>
                <Table size="sm">
                  <tbody>
                    <tr>
                      <th>{t("recipient") || "Recipient"}</th>
                      <td>{previewData.recipient}</td>
                    </tr>
                    <tr>
                      <th>{t("language") || "Language"}</th>
                      <td>{previewData.language?.toUpperCase()}</td>
                    </tr>
                    <tr>
                      <th>{t("subject") || "Subject"}</th>
                      <td>{previewData.previewSubject}</td>
                    </tr>
                  </tbody>
                </Table>

                <h5 className="mt-3">{t("variables") || "Variables"}</h5>
                <Table size="sm">
                  <tbody>
                    {Object.entries(previewData.variables || {}).map(([key, value]) => (
                      <tr key={key}>
                        <th>
                          <code>{`{{${key}}}`}</code>
                        </th>
                        <td className="text-break small">{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {!previewData.templateFound && (
                  <Alert color="warning">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    {t("templateNotFound") || "Template not found for this trigger"}
                  </Alert>
                )}
                {previewData.templateFound && !previewData.templateActive && (
                  <Alert color="warning">
                    <i className="fas fa-pause-circle mr-2"></i>
                    {t("templateDisabled") || "This template is disabled"}
                  </Alert>
                )}
              </Col>
              <Col md={8}>
                <h5>{t("preview") || "Preview"}</h5>
                {previewData.previewHtml ? (
                  <iframe
                    srcDoc={previewData.previewHtml}
                    style={{
                      width: "100%",
                      height: "500px",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                    }}
                    title="Email Preview"
                  />
                ) : (
                  <Alert color="secondary">{t("noPreviewAvailable") || "No preview available"}</Alert>
                )}
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={() => {
              handleSendTest(selectedTrigger);
              setPreviewModalOpen(false);
            }}
            disabled={isSending || !recipientEmail}
          >
            <i className="fas fa-paper-plane mr-2"></i>
            {t("sendTest") || "Send Test"}
          </Button>
          <Button color="secondary" onClick={() => setPreviewModalOpen(false)}>
            {t("close") || "Close"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default EmailTesting;
